import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const MEDIA_BASE = BACKEND_URL;

const api = axios.create({ baseURL: API_BASE });

export const formatTC = (sec) => {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const f = Math.floor((sec - Math.floor(sec)) * 30);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}:${String(f).padStart(2, "0")}`;
};

export const formatDuration = (sec) => {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const resolveMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  // /media/... は ingress では backend に届かない（frontendにフォールバックされる）。
  // /api/media/... に書き換えて backend (port 8001) にルーティングさせる。
  let p = path;
  if (p.startsWith("/media/")) p = "/api" + p;
  return `${MEDIA_BASE}${p}`;
};

export const resolveMaterialUrl = (material) => {
  if (!material) return null;
  // Drive動画は Google Drive ネイティブの iframe プレビュー URL を返す（即時再生・転送負荷ゼロ）
  // ※ <video> タグでは使えない。iframe で埋め込む前提
  if (material.drive_file_id && material.is_video) {
    return `https://drive.google.com/file/d/${material.drive_file_id}/preview`;
  }
  if (material.drive_file_id) {
    return `${API_BASE}/drive/proxy/${material.drive_file_id}`;
  }
  return resolveMediaUrl(material.file_url);
};

// 通常 <video> タグ用の Drive 動画ストリーミング URL（必要な時のみ使用）
export const resolveDriveStreamUrl = (material) => {
  if (!material || !material.drive_file_id) return null;
  return `${API_BASE}/drive/proxy/${material.drive_file_id}`;
};

// 軽量MP4プロキシ（480p H.264 ローカルキャッシュ）の <video> 用URL
// proxy_status === "ready" のときのみ返却。それ以外は null
export const resolveProxyVideoUrl = (material) => {
  if (!material || !material.is_video) return null;
  if (material.proxy_status !== "ready") return null;
  return `${API_BASE}/materials/${material.id}/proxy`;
};

// Proxy management
export const getMaterialProxyStatus = (mid) => api.get(`/materials/${mid}/proxy/status`).then(r => r.data);
export const regenerateMaterialProxy = (mid) => api.post(`/materials/${mid}/proxy/regenerate`).then(r => r.data);
export const generateMissingProxies = (pid) => api.post(`/projects/${pid}/proxies/generate_missing`).then(r => r.data);
export const getProjectProxyStatus = (pid) => api.get(`/projects/${pid}/proxies/status`).then(r => r.data);

// Templates
export const fetchStorylines = () => api.get("/templates/storylines").then(r => r.data);
export const fetchEffectPresets = () => api.get("/templates/effects").then(r => r.data);
export const fetchCategories = () => api.get("/templates/categories").then(r => r.data);

// Projects
export const listProjects = () => api.get("/projects").then(r => r.data);
export const createProject = (payload) => api.post("/projects", payload).then(r => r.data);
export const getProject = (id) => api.get(`/projects/${id}`).then(r => r.data);
export const updateProject = (id, payload) => api.put(`/projects/${id}`, payload).then(r => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then(r => r.data);
export const duplicateProject = (id, new_name) => api.post(`/projects/${id}/duplicate`, { new_name }).then(r => r.data);

// Chapters
export const listChapters = (pid) => api.get(`/projects/${pid}/chapters`).then(r => r.data);
export const updateChapter = (id, payload) => api.put(`/chapters/${id}`, payload).then(r => r.data);
export const deleteChapter = (id) => api.delete(`/chapters/${id}`).then(r => r.data);
export const addChapter = (pid, title, order) => api.post(`/projects/${pid}/chapters`, { title, order }).then(r => r.data);

// Cuts
export const listCuts = (pid) => api.get(`/projects/${pid}/cuts`).then(r => r.data);
export const addCut = (pid, payload) => api.post(`/projects/${pid}/cuts`, payload).then(r => r.data);
export const updateCut = (id, payload) => api.put(`/cuts/${id}`, payload).then(r => r.data);
export const deleteCut = (id) => api.delete(`/cuts/${id}`).then(r => r.data);
export const assignMaterialToCut = (cutId, materialId, inPoint = 0) =>
  api.post(`/cuts/${cutId}/assign/${materialId}?in_point=${inPoint}`).then(r => r.data);
export const unassignMaterial = (cutId) => api.post(`/cuts/${cutId}/unassign`).then(r => r.data);

// Materials
export const listMaterials = (pid, category) =>
  api.get(`/projects/${pid}/materials${category && category !== "all" ? `?category=${encodeURIComponent(category)}` : ""}`).then(r => r.data);
export const uploadMaterial = (pid, file, category, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  return api.post(`/projects/${pid}/materials/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  }).then(r => r.data);
};
export const importStockMaterial = (pid, payload) =>
  api.post(`/projects/${pid}/materials/import_stock`, payload).then(r => r.data);
export const updateMaterial = (id, payload) => api.put(`/materials/${id}`, payload).then(r => r.data);
export const deleteMaterial = (id) => api.delete(`/materials/${id}`).then(r => r.data);

// Stock search
export const searchStock = (query, source = "pexels") =>
  api.get(`/stock/search?query=${encodeURIComponent(query)}&source=${source}`).then(r => r.data);

// Players
export const listPlayers = (pid) => api.get(`/projects/${pid}/players`).then(r => r.data);
export const createPlayer = (pid, payload) => api.post(`/projects/${pid}/players`, payload).then(r => r.data);
export const updatePlayer = (id, payload) => api.put(`/players/${id}`, payload).then(r => r.data);
export const deletePlayer = (id) => api.delete(`/players/${id}`).then(r => r.data);
export const uploadPlayerPhoto = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/players/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// BGM
export const listBgm = (pid) => api.get(`/projects/${pid}/bgm`).then(r => r.data);
export const uploadBgm = (pid, file, name, start_at = 0, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("name", name || file.name);
  fd.append("start_at", String(start_at));
  return api.post(`/projects/${pid}/bgm/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  }).then(r => r.data);
};
export const updateBgm = (id, payload) => api.put(`/bgm/${id}`, payload).then(r => r.data);
export const deleteBgm = (id) => api.delete(`/bgm/${id}`).then(r => r.data);
export const syncCutsToBeats = (pid, bgm_id) => api.post(`/projects/${pid}/bgm/sync_to_beats`, { bgm_id }).then(r => r.data);

// AI
export const aiGenerateCaption = (cutId) => api.post(`/cuts/${cutId}/ai/caption`).then(r => r.data);
export const aiGenerateNarration = (chapterId) => api.post(`/chapters/${chapterId}/ai/narration`).then(r => r.data);
export const aiCompositionAdvice = (pid) => api.post(`/projects/${pid}/ai/composition_advice`).then(r => r.data);
export const aiSearchKeywords = (chapterId) => api.post(`/chapters/${chapterId}/ai/keywords`).then(r => r.data);
export const aiSuggestMaterials = (cutId) => api.post(`/cuts/${cutId}/ai/suggest_materials`).then(r => r.data);

// Versions
export const createSnapshot = (pid, label = "") => api.post(`/projects/${pid}/versions/snapshot`, { label }).then(r => r.data);
export const listVersions = (pid) => api.get(`/projects/${pid}/versions`).then(r => r.data);
export const restoreVersion = (vid) => api.post(`/versions/${vid}/restore`).then(r => r.data);

// Export
export const exportFcpxmlUrl = (pid, driveLocalMount = "") => {
  const base = `${API_BASE}/projects/${pid}/export/fcpxml`;
  if (driveLocalMount) {
    return `${base}?drive_local_mount=${encodeURIComponent(driveLocalMount)}`;
  }
  return base;
};
export const exportStorylineJsonUrl = (pid) => `${API_BASE}/projects/${pid}/export/storyline_json`;

// API additions for new features
export const fetchIntroTemplates = () => api.get("/intro_templates").then(r => r.data);
export const buildPlayerIntros = (pid, payload) => api.post(`/projects/${pid}/build_player_intros`, payload).then(r => r.data);
export const uploadPlayerIntroVideo = (playerId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/players/${playerId}/intro_video`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const createCustomTemplateFromImage = (file, styleNotes) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("style_notes", styleNotes || "");
  return api.post(`/intro_templates/custom_from_image`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const listCustomTemplates = () => api.get(`/intro_templates/custom`).then(r => r.data);
export const deleteCustomTemplate = (id) => api.delete(`/intro_templates/custom/${id}`).then(r => r.data);

export const analyzeMaterial = (id) => api.post(`/materials/${id}/analyze`).then(r => r.data);
export const analyzeAllMaterials = (pid, only_pending = true) => api.post(`/projects/${pid}/materials/analyze_all?only_pending=${only_pending}`).then(r => r.data);
export const getAnalysisStatus = (pid) => api.get(`/projects/${pid}/analysis_status`).then(r => r.data);
export const getAnalysisCostPreview = (pid, only_pending = true) => api.get(`/projects/${pid}/analysis_cost_preview?only_pending=${only_pending}`).then(r => r.data);
export const autoAssignMaterials = (pid, overwrite = false) => api.post(`/projects/${pid}/auto_assign_materials`, { overwrite }).then(r => r.data);
export const listMaterialsByPlayer = (pid, playerId) => api.get(`/projects/${pid}/materials/by_player/${playerId}`).then(r => r.data);

export const startRenderMp4 = (pid) => api.post(`/projects/${pid}/export/render_mp4`).then(r => r.data);
export const getRenderJob = (jobId) => api.get(`/render_jobs/${jobId}`).then(r => r.data);

// Google Drive
export const driveStatus = () => api.get(`/drive/status`).then(r => r.data);
export const driveConnect = () => api.get(`/drive/connect`).then(r => r.data);
export const driveDisconnect = () => api.post(`/drive/disconnect`).then(r => r.data);
export const driveListFolders = (parent_id = "root") => api.get(`/drive/folders?parent_id=${parent_id}`).then(r => r.data);
export const driveListFiles = (folder_id, recursive = false, video_only = false) =>
  api.get(`/drive/files?folder_id=${folder_id}&recursive=${recursive}&video_only=${video_only}`).then(r => r.data);
export const driveSetupProjectStructure = (payload) => api.post(`/drive/setup_project_structure`, payload).then(r => r.data);
export const driveImportFolder = (pid, folder_id, recursive = true, auto_analyze = false) =>
  api.post(`/projects/${pid}/drive/import_folder`, { folder_id, recursive, auto_analyze }).then(r => r.data);
export const driveSyncPlayersFolder = (pid, folder_id) =>
  api.post(`/projects/${pid}/drive/sync_players_folder`, { folder_id }).then(r => r.data);
export const driveAdoptExistingRoot = (pid, folder_id, project_name = null, auto_sync_players = true) =>
  api.post(`/projects/${pid}/drive/adopt_existing_root`, { folder_id, project_name, auto_sync_players }).then(r => r.data);

export const driveScanNew = (pid) => api.get(`/projects/${pid}/drive/scan_new`).then(r => r.data);
export const driveImportAllNew = (pid, autoAnalyze = true) => api.post(`/projects/${pid}/drive/import_all_new`, { auto_analyze: autoAnalyze }).then(r => r.data);

export const getTemplateSuggestions = (cutId) => api.get(`/cuts/${cutId}/template_suggestions`).then(r => r.data);
export const adoptTemplateIntent = (cutId, intent) => api.post(`/cuts/${cutId}/template_intent`, intent).then(r => r.data);
export const deleteTemplateIntent = (cutId, intentId) => api.delete(`/cuts/${cutId}/template_intent/${intentId}`).then(r => r.data);

export const splitCut = (cutId, parts = 2) => api.post(`/cuts/${cutId}/split`, { parts }).then(r => r.data);
export const mergeNextCut = (cutId) => api.post(`/cuts/${cutId}/merge_next`).then(r => r.data);

export const downloadPremiereGuide = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/premiere_finishing_guide`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `premiere_finishing_guide_${projectId.slice(0,8)}.md`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default api;
